import angular from 'angular';
import '../../shared.module';
import template from './recaptcha.component.html';
import './recaptcha.component.scss';

export const RECAPTCHA_ONLOAD_CALLBACK = '__cpRecaptchaOnload';
export const RECAPTCHA_SCRIPT_URL = `https://www.google.com/recaptcha/api.js?render=explicit&hl=pt-BR&onload=${RECAPTCHA_ONLOAD_CALLBACK}`;
const LOAD_TIMEOUT_MS = 15000;
export const RECAPTCHA_NORMAL_WIDTH = 304;
const LOAD_ERROR = 'Não foi possível carregar a verificação de segurança. Verifique sua conexão.';
const WIDGET_ERROR = 'A verificação de segurança falhou. Tente novamente.';

let pendingLoad = null;

export function resetRecaptchaLoader() {
  pendingLoad = null;
}

export function loadRecaptcha($window, $document, $q, $timeout) {
  if ($window.grecaptcha && typeof $window.grecaptcha.render === 'function') {
    return $q.resolve($window.grecaptcha);
  }
  if (pendingLoad) return pendingLoad;

  const deferred = $q.defer();
  const script = $document[0].createElement('script');
  const fail = () => {
    $timeout.cancel(timer);
    script.remove();
    delete $window[RECAPTCHA_ONLOAD_CALLBACK];
    pendingLoad = null;
    deferred.reject(new Error(LOAD_ERROR));
  };
  const timer = $timeout(fail, LOAD_TIMEOUT_MS);
  $window[RECAPTCHA_ONLOAD_CALLBACK] = () => {
    $timeout.cancel(timer);
    deferred.resolve($window.grecaptcha);
  };
  script.src = RECAPTCHA_SCRIPT_URL;
  script.async = true;
  script.defer = true;
  script.onerror = fail;
  $document[0].head.appendChild(script);
  pendingLoad = deferred.promise;
  return pendingLoad;
}

class RecaptchaController {
  constructor($element, $window, $document, $q, $timeout, $scope, ThemeService) {
    this.$element = $element;
    this.$window = $window;
    this.$document = $document;
    this.$q = $q;
    this.$timeout = $timeout;
    this.$scope = $scope;
    this.ThemeService = ThemeService;
    this.state = 'loading';
    this.expired = false;
    this.errorMessage = '';
    this.widgetId = null;
    this.grecaptcha = null;
  }

  $postLink() {
    this.linked = true;
    if (this.onReady) this.onReady({ api: { reset: () => this.reset() } });
    this.start();
  }

  $onChanges(changes) {
    if (this.linked && changes.unavailable) this.start();
  }

  start() {
    if (this.unavailable) return this.fail(LOAD_ERROR);
    return this.load();
  }

  retry() {
    if (this.unavailable && this.onRetry) return this.onRetry();
    return this.load();
  }

  load() {
    this.state = 'loading';
    this.errorMessage = '';
    return loadRecaptcha(this.$window, this.$document, this.$q, this.$timeout).then(
      (grecaptcha) => this.render(grecaptcha),
      (error) => this.fail(error.message)
    );
  }

  render(grecaptcha) {
    this.grecaptcha = grecaptcha;
    if (this.widgetId !== null) {
      this.reset();
      this.state = 'ready';
      return;
    }
    const availableWidth = this.$element[0].clientWidth;
    try {
      this.widgetId = grecaptcha.render(this.$element[0].querySelector('.cp-recaptcha__widget'), {
        sitekey: this.siteKey,
        theme: this.ThemeService.current === 'dark' ? 'dark' : 'light',
        size: availableWidth && availableWidth < RECAPTCHA_NORMAL_WIDTH ? 'compact' : 'normal',
        callback: (token) => this.$scope.$applyAsync(() => this.solved(token)),
        'expired-callback': () => this.$scope.$applyAsync(() => this.expire()),
        'error-callback': () => this.$scope.$applyAsync(() => this.fail(WIDGET_ERROR))
      });
      this.state = 'ready';
    } catch {
      this.fail(LOAD_ERROR);
    }
  }

  solved(token) {
    this.expired = false;
    this.emit(token);
  }

  expire() {
    this.expired = true;
    this.emit(null);
  }

  fail(message) {
    this.state = 'error';
    this.errorMessage = message;
    this.emit(null);
  }

  reset() {
    this.expired = false;
    if (this.grecaptcha && this.widgetId !== null) this.grecaptcha.reset(this.widgetId);
    this.emit(null);
  }

  emit(token) {
    if (this.onToken) this.onToken({ token });
  }
}

RecaptchaController.$inject = [
  '$element',
  '$window',
  '$document',
  '$q',
  '$timeout',
  '$scope',
  'ThemeService'
];

angular.module('app.shared').component('cpRecaptcha', {
  bindings: {
    siteKey: '<',
    unavailable: '<',
    onRetry: '&',
    onToken: '&',
    onReady: '&'
  },
  controller: RecaptchaController,
  controllerAs: 'vm',
  template
});
